import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LuSend } from "react-icons/lu";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Success from "./Success";
import { handleContact, verifyToken } from "../../utils/api";
import { useMutation } from "react-query";
import ReCAPTCHA from "react-google-recaptcha";
import Codes from "./Codes";

const reasons = ["support", "query", "business", "feedback", "other"];

const userCountryCode = (localStorage.getItem("user-localev2")) ? "+"+JSON.parse(localStorage.getItem("user-localev2")).calling_code : "+44";

const userLanguage = localStorage.getItem("user-prefsv2") ? JSON.parse(localStorage.getItem("user-prefsv2")).language : "en";

const RightSection = () => {
  const { t } = useTranslation();

  const [countryCode, setCountryCode] = React.useState(userCountryCode);

  const schema = yup.object().shape({
    name: yup.string().required(t("product.reviewform.required")),
    email: yup
      .string()
      .email("Must be a Valid Email")
      .required(t("product.reviewform.required")),
    phone: yup.string(),
    message: yup.string().required(t("product.reviewform.required")),
    reason: yup.string().required(t("product.reviewform.required")),
    terms: yup.boolean().oneOf([true], t("product.reviewform.required")),
  });

  const captchaRef = React.useRef(null);
  const [captchaError, setCaptchaError] = React.useState(null);

  const verify = useMutation({
    mutationFn: verifyToken,
  });

  React.useEffect(() => {
    setValue("email", localStorage.getItem("safetag-username") || "");
  }, []);

  const { register, formState, setValue, watch, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
  });

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setValue("reason", urlParams.get("reason"));
  }, []);

  const reason = watch("reason");
  const terms = watch("terms");

  const contact = useMutation({
    mutationFn: handleContact,
  });

  const onSubmit = async (data) => {
    //Append countryCode to phone if data.phone is not undefined
    data.phone = data.phone ? (countryCode + " " + data.phone) : undefined
    console.log(data);
    const token = captchaRef.current.getValue();
    captchaRef.current.reset();
    if (token) {
      await verify.mutateAsync({ token });
      if (verify.error) {
        setCaptchaError(true);
      } else {
        contact.mutate({
          data: {
            email: data.email,
            name: data.name,
            message: data.message,
            reason: data.reason,
            // Append countryCode to phone if data.phone is not undefined
            phone: data.phone,
            //Add language to data
            language: userLanguage
          },
        });
      }
    } else {
      setCaptchaError(true);
    }
  };

  if (contact.isSuccess) return <Success />;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="py-8 md:px-8 md:border bg-white flex flex-col gap-6 rounded-lg"
    >
      <div className="flex flex-col gap-1">
        <label>{t("contact.form.help")}</label>
        <div className="hidden lg:flex flex-row gap-2 flex-wrap">
          {reasons.map((a, i) => (
            <div
              onClick={() => setValue("reason", a)}
              key={i}
              className={`px-4 py-2 rounded-lg cursor-pointer transition-all duration-300 ${
                reason === a
                  ? "bg-[#8B33331A] text-pr border border-[#8B33331A] hover:bg-[#8B33331A]"
                  : "bg-white text-black text-opacity-50 border hover:bg-gray-100"
              }`}
            >
              {t(`contact.form.${a}`)}
            </div>
          ))}
        </div>
        <select
          className={`py-2 px-3 rounded-lg border outline-none transition-all duration-300 block lg:hidden  ${
            formState.errors.reason
              ? "border-red-500"
              : "focus:border-[#0006] border-gray-200"
          }`}
          {...register("reason")}
        >
          <option disabled value="">
            {t("contact.form.select_reason")}
          </option>
          {reasons.map((a, i) => (
            <option key={i} value={a}>
              {t(`contact.form.${a}`)}
            </option>
          ))}
        </select>
        <p className="text-red-600 text-sm">
          {formState.errors?.reason?.message}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <label>{t("contact.form.name_label")}</label>
        <input
          {...register("name")}
          className={`py-2 px-3 rounded-lg border outline-none transition-all duration-300 ${
            formState.errors.name ? "border-red-500" : "border-[#e5e7eb] focus:border-[#0006]"
          }`}
          placeholder={t("contact.form.name_placeholder")}
        />
        <p className="text-red-600 text-sm">
          {formState.errors?.name?.message}
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <label>{t("contact.form.email_label")}</label>
        <input
          {...register("email")}
          className={`py-2 px-3 rounded-lg border outline-none shadow-none transition-all duration-300 ${
            formState.errors.email ? "border-red-500" : "border-[#e5e7eb] focus:border-[#0006]"
          }`}
          placeholder={t("contact.form.email_placeholder")}
        />
        <p className="text-red-600 text-sm">
          {formState.errors?.email?.message}
        </p>
      </div>
      {reason === "business" && (
        <div className="flex flex-col gap-1">
          <label>{t("contact.form.phone_label")}</label>
          <div className="flex flex-row gap-1">
            <Codes
              value={countryCode}
              name="country-code"
              onChange={setCountryCode}
            />
            <input
              {...register("phone")}
              className={`py-2 px-3 flex-1 rounded-lg border outline-none shadow-none ${
                formState.errors.phone
                  ? "border-red-500"
                  : "focus:border-[#0006]"
              } transition-all duration-300`}
              placeholder="123-456-7890"
              autoComplete="tel-national"
              name="phone"
              id="phone"
              type="tel"
            />
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <label>{t("contact.form.message_label")}</label>
        <textarea
          {...register("message")}
          className={`py-2 px-3 rounded-lg border no-outline-force shadow-none border-[#e5e7eb] h-48 focus:border-[#0006] transition-all duration-300 ${
            formState.errors.message ? "border-red-500" : "focus:border-[#0006]"
          }`}
          placeholder={t("contact.form.message_placeholder")}
        />
        <p className="text-red-600 text-sm">
          {formState.errors?.message?.message}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-row gap-2 items-center">
          <input
            type="checkbox"
            {...register("terms")}
            className="w-5 h-5 text-sr rounded border-2 border-gray-600 shadow-sm focus:border-pr focus:ring focus:ring-offset-0 focus:ring-pr focus:ring-opacity-50"
          />
          <label className="text-black text-opacity-70">
            {t("contact.form.terms1")}{" "}
            <Link to="/tos" className="font-semibold">
              {t("contact.form.terms2")}
            </Link>
          </label>
        </div>
        <p className="text-red-600 text-sm">
          {formState.errors?.terms?.message}
        </p>
      </div>
      {contact.isError ? (
        <div className="text-red-500 border p-3 border-[#FF6464] rounded bg-[#FF64641A] ">
          {t("contact.form.error")}
        </div>
      ) : null}
      {terms && !contact.isLoading && (
        <div className="w-full flex justify-center items-center">
          <ReCAPTCHA sitekey={import.meta.env.VITE_SITE_KEY} ref={captchaRef} />
        </div>
      )}

      {terms && captchaError && (
        <div className="w-full flex justify-center items-center">
          <p className="text-xs text-red-500  ml-1">
            {t("signup.main.error_captcha")}
          </p>
        </div>
      )}
      <button
        disabled={contact.isLoading}
        type="submit"
        className="flex flex-row gap-3 w-full justify-center items-center disabled:bg-gray-600 text-white bg-pr hover:bg-sr transition-all duration-300 py-3 rounded-lg"
      >
        {contact.isLoading ? (
          t("contact.main.button_sending")
        ) : (
          <>
            <p>{t("contact.form.submit")}</p>
            <LuSend className="rotate-45 text-xl" />
          </>
        )}
      </button>
    </form>
  );
};

export default RightSection;
